import type { CalendarEvent } from '@highlight/common'
import type { Person } from '@renderer/chat/hooks/usePeople'

const NAMES_SHOWN = 2

/** Rooms and equipment are attendees on Google Calendar too. */
function isResourceEmail(email: string): boolean {
  return email.endsWith('resource.calendar.google.com')
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

function firstNameFor(email: string, person: Person | undefined): string {
  const given = person?.givenName?.trim()
  if (given) return given
  const display = person?.displayName?.trim().split(/\s+/)[0]
  if (display) return display
  return capitalize(email.split('@')[0].split(/[._+-]/)[0] ?? email)
}

/** Everyone on the meeting but you and its rooms, as lowercased emails. */
export function listOtherAttendees(meeting: Pick<CalendarEvent, 'attendees' | 'userEmail'>): Array<string> {
  const self = meeting.userEmail?.trim().toLowerCase()
  return meeting.attendees
    .map((attendee) => attendee.email.trim().toLowerCase())
    .filter((email) => email && email !== self && !isResourceEmail(email))
}

/** Those attendees by first name: "Sarah, Rumi +3", or "Just you" for a solo hold. */
export function summarizeAttendees(others: Array<string>, peopleByEmail: ReadonlyMap<string, Person>): string {
  if (others.length === 0) return 'Just you'

  const names = others.slice(0, NAMES_SHOWN).map((email) => firstNameFor(email, peopleByEmail.get(email)))
  const overflow = others.length - names.length
  return overflow > 0 ? `${names.join(', ')} +${overflow}` : names.join(', ')
}
